// ============================================================================
//  DepensesPages.tsx — Module Dépenses (notes de frais) NexusCorp ERP
//  Étape 5b-1 : Liste + Détail (consultation seule).
//  Les actions (créer / valider / payer) arrivent à l'étape 5b-2.
// ============================================================================

import { useState, useRef } from 'react'
import { toNum } from '@/lib/decimal'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, Routes, Route } from 'react-router-dom'
import {
  Wallet, Search, ArrowLeft, ChevronRight, ChevronLeft, FileText,
  Clock, Send, CheckCircle, XCircle, Banknote, ShieldCheck, Paperclip, Lock,
  Plus, Check, X, Upload, Trash2, Tags, Pencil, Ban, RotateCcw, Eye, AlertTriangle, Download,
} from 'lucide-react'
import { FilePreviewModal, downloadFromUrl } from '@/components/ui'
import {
  depensesApi,
  type ExpenseRequest, type ExpenseStatus, type ExpenseAttachment,
  type ExpensePaymentMode, type ListExpensesParams, type CreateExpenseInput,
  type ExpenseCategory,
} from '@/modules/depenses/api/depenses.api'
import { accountingApi } from '@/modules/accounting/api/accounting.api'
import { usePermission } from '@/modules/auth/hooks/usePermission'
import { useAuthStore } from '@/stores/auth.store'
import { useNotificationStore } from '@/stores/notification.store'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtMoney = (n?: any) =>
  n !== undefined && n !== null
    ? new Intl.NumberFormat('fr-SN', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }).format(toNum(n))
    : '—'

const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('fr-FR') : '—'
const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

/** Affiche un identifiant technique de façon compacte (les noms nécessitent un
 *  enrichissement backend — cf. ticket B28). */
const shortId = (id?: string | null) => id ? id.slice(0, 8) + '…' : '—'

const fmtSize = (bytes: number) =>
  bytes < 1024 ? `${bytes} o` : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} Ko` : `${(bytes / 1024 / 1024).toFixed(1)} Mo`

const PAYMENT_LABELS: Record<string, string> = { CASH: 'Espèces (caisse)', BANK: 'Virement (banque)' }

// Validation d'upload côté client (confort — le backend reste la barrière).
// Alignée sur l'allowlist partagée du backend (shared/upload/file-upload.util).
const ATTACH_MAX_MB = 50
const ATTACH_ALLOWED_EXT = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'txt', 'csv', 'zip']
const ATTACH_ACCEPT = ATTACH_ALLOWED_EXT.map(e => '.' + e).join(',')

/** États où l'on peut joindre un justificatif (miroir backend) : tous sauf REJECTED.
 *  Après paiement, la pièce est reçue tardivement (qui peut joindre : cf. canAttachNow). */
function canAttachExpense(exp: ExpenseRequest): boolean {
  return exp.status !== 'REJECTED'
}

/** Fiche payée (PAID / JUSTIFIED) : les justificatifs ajoutés sont « reçus après paiement ». */
const isPaidPhase = (status: ExpenseStatus) => status === 'PAID' || status === 'JUSTIFIED'

/** État d'archivage légal d'une pièce, par ordre de priorité (champs dérivés backend).
 *  `isWormSealed` n'est qu'un repli pour une réponse ancienne (jamais vrai en pratique). */
const ARCHIVE_BADGES = {
  sealed: {
    label: 'Scellé',
    hint:  "Archivé dans l'archive légale WORM",
    color: 'bg-[var(--green-bg)] text-[var(--green-text)] border-[rgba(22,163,74,0.3)]',
    icon:  Lock,
  },
  pending: {
    label: 'En attente de scellement',
    hint:  "Rattaché à l'écriture comptable, scellé à sa validation",
    color: 'bg-[var(--indigo-bg)] text-info-700 border-info-500/30',
    icon:  Clock,
  },
  notArchived: {
    label: 'Non archivé',
    hint:  "Écriture comptable non modifiable ou absente : pièce conservée, hors archive légale",
    color: 'bg-[var(--amber-bg)] text-[var(--amber-text)] border-[rgba(245,158,11,0.3)]',
    icon:  AlertTriangle,
  },
} as const

function archiveState(j: ExpenseAttachment, status: ExpenseStatus): keyof typeof ARCHIVE_BADGES | null {
  if (j.sealed ?? j.isWormSealed) return 'sealed'
  if (j.attachedToEntry) return 'pending'
  // Fiche payée : toute pièce non rattachée à l'écriture est hors archive légale
  // (reçue après validation, fiche sans écriture, payée avant le rattachement…).
  if (isPaidPhase(status) && j.attachedToEntry === false) return 'notArchived'
  if (j.receivedAfterPayment) return 'notArchived'
  return null
}

/** Badge d'archivage : libellé visible + explication (infobulle et lecteur d'écran). */
function ArchiveBadge({ state }: { state: keyof typeof ARCHIVE_BADGES }) {
  const b = ARCHIVE_BADGES[state]
  const Icon = b.icon
  return (
    <span
      title={b.hint}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap shrink-0 ${b.color}`}
    >
      <Icon className="w-3 h-3" aria-hidden="true" />
      {b.label}
      <span className="sr-only"> : {b.hint}</span>
    </span>
  )
}

const STATUS_CONFIG: Record<ExpenseStatus, { label: string; color: string; icon: typeof Clock }> = {
  DRAFT:     { label: 'Brouillon', color: 'bg-[#D6D2EC]/20 text-[var(--tx2)] border-[var(--tx3)]/30',        icon: Clock },
  SUBMITTED: { label: 'Soumise',   color: 'bg-[#BAE6FD] text-blue-300 border-blue-500/30',                   icon: Send },
  APPROVED:  { label: 'Validée',   color: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',           icon: CheckCircle },
  REJECTED:  { label: 'Rejetée',   color: 'bg-red-500/20 text-red-300 border-[rgba(239,68,68,0.35)]',        icon: XCircle },
  PAID:      { label: 'Payée',     color: 'bg-[#FEF3C7] text-[#B45309] border-[rgba(245,158,11,0.3)]',       icon: Banknote },
  JUSTIFIED: { label: 'Justifiée', color: 'bg-[#10B981]/20 text-emerald-300 border-emerald-500/30',          icon: ShieldCheck },
}

const Spinner = () => <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />

function StatusBadge({ status }: { status: ExpenseStatus }) {
  const c = STATUS_CONFIG[status]
  if (!c) return null
  const Icon = c.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${c.color}`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  )
}

/** « Prénom Nom » d'un acteur ou d'un demandeur ; identifiant abrégé en repli. */
function personName(p: { firstName: string; lastName: string } | null | undefined, fallbackId?: string | null): string {
  const name = p ? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() : ''
  return name || shortId(fallbackId)
}

/** Demandeur affiché en liste/détail : nom de l'employé, identifiant abrégé en repli. */
function requester(exp: ExpenseRequest): string {
  if (exp.type === 'FACTURE_FOURNISSEUR') return 'Fournisseur direct'
  if (!exp.employeeId) return '—'
  return personName(exp.employee, exp.employeeId)
}

/** Vrai si le demandeur est affiché sous forme d'identifiant (police mono). */
const requesterIsId = (exp: ExpenseRequest) =>
  exp.type !== 'FACTURE_FOURNISSEUR' && !!exp.employeeId && !exp.employee

// ============================================================================
//  LISTE
// ============================================================================

function DepensesListPage() {
  const navigate = useNavigate()
  const { can } = usePermission()
  const [params, setParams] = useState<ListExpensesParams>({ page: 1, limit: 20 })
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['depenses', params, search],
    queryFn:  () => depensesApi.list({ ...params, search: search || undefined }),
  })

  const STATUS_FILTERS: Array<{ value?: ExpenseStatus; label: string }> = [
    { label: 'Toutes' },
    { value: 'DRAFT',     label: 'Brouillons' },
    { value: 'SUBMITTED', label: 'Soumises' },
    { value: 'APPROVED',  label: 'Validées' },
    { value: 'PAID',      label: 'Payées' },
    { value: 'JUSTIFIED', label: 'Justifiées' },
    { value: 'REJECTED',  label: 'Rejetées' },
  ]

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Wallet className="w-5 h-5 text-[var(--brand-dark)]" />
          <h1 className="text-xl font-bold text-[var(--brand-dark)]">Dépenses</h1>
          {data && <span className="text-xs text-[var(--tx3)] bg-white px-2 py-0.5 rounded-full">{data.meta.total}</span>}
        </div>
        {can('depenses:create') && (
          <button
            onClick={() => navigate('/depenses/nouvelle')}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvelle
          </button>
        )}
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-[var(--tx3)] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par référence, motif…"
            className="w-full pl-9 pr-4 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] placeholder-[var(--tx3)] focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="flex items-center gap-1">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.label}
              onClick={() => setParams(p => ({ ...p, status: f.value, page: 1 }))}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                params.status === f.value
                  ? 'bg-[var(--blue)] text-white'
                  : 'bg-white text-[var(--tx3)] hover:text-[var(--brand-dark)] hover:bg-[var(--surface-2)]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center p-12"><Spinner /></div>
        ) : !data?.data.length ? (
          <div className="p-12 text-center">
            <Wallet className="w-8 h-8 text-[var(--tx3)] mx-auto mb-3" />
            <p className="text-[var(--tx3)]">Aucune dépense trouvée</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Référence', 'Motif', 'Demandeur', 'Montant', 'Statut', 'Date', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs text-[var(--tx3)] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.data.map(exp => (
                <tr
                  key={exp.id}
                  onClick={() => navigate(`/depenses/${exp.id}`)}
                  className="border-b border-[var(--bd)] cursor-pointer transition-colors hover:bg-[var(--surface-2)]"
                >
                  <td className="px-4 py-3"><span className="font-mono text-blue-300 text-xs">{exp.reference}</span></td>
                  <td className="px-4 py-3 text-[var(--tx2)] max-w-[280px] truncate" title={exp.description}>{exp.description}</td>
                  <td className={`px-4 py-3 ${requesterIsId(exp) ? 'font-mono text-xs text-[var(--tx3)]' : 'text-[var(--tx2)]'}`}>{requester(exp)}</td>
                  <td className="px-4 py-3 font-bold text-[var(--brand-dark)]">{fmtMoney(exp.amount)}</td>
                  <td className="px-4 py-3"><StatusBadge status={exp.status} /></td>
                  <td className="px-4 py-3 text-xs text-[var(--tx3)]">{fmtDate(exp.createdAt)}</td>
                  <td className="px-4 py-3"><ChevronRight className="w-4 h-4 text-[var(--tx3)]" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {data && data.meta.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--bd)]">
            <p className="text-xs text-[var(--tx3)]">
              {((data.meta.page - 1) * data.meta.limit) + 1}–{Math.min(data.meta.page * data.meta.limit, data.meta.total)} sur {data.meta.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                disabled={data.meta.page <= 1}
                onClick={() => setParams(p => ({ ...p, page: (p.page ?? 1) - 1 }))}
                className="p-1.5 rounded-lg bg-white text-[var(--tx2)] disabled:opacity-40 hover:bg-[var(--surface-2)] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-[var(--tx3)]">{data.meta.page} / {data.meta.totalPages}</span>
              <button
                disabled={data.meta.page >= data.meta.totalPages}
                onClick={() => setParams(p => ({ ...p, page: (p.page ?? 1) + 1 }))}
                className="p-1.5 rounded-lg bg-white text-[var(--tx2)] disabled:opacity-40 hover:bg-[var(--surface-2)] transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
//  DÉTAIL
// ============================================================================

function InfoRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">{label}</p>
      <p className={`text-sm text-[var(--brand-dark)] ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
    </div>
  )
}

function DepensesDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()
  const { can } = usePermission()
  const currentUser = useAuthStore(s => s.user)
  const { addNotification } = useNotificationStore()

  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [payOpen, setPayOpen] = useState(false)
  const [chargeAccountId, setChargeAccountId] = useState('')

  const { data: exp, isLoading, isError } = useQuery({
    queryKey: ['depense', id],
    queryFn:  () => depensesApi.getOne(id!),
    enabled:  !!id,
  })

  // Comptes de charge classe 6 pour le sélecteur de paiement (API compta existante)
  const { data: chargeAccounts } = useQuery({
    queryKey: ['charge-accounts'],
    queryFn:  () => accountingApi.getAccounts({ class: 6, isActive: true }),
    enabled:  payOpen,
  })

  // Catégories : résolution du libellé sur la fiche + pré-remplissage du compte au paiement.
  const { data: categories } = useQuery({
    queryKey: ['depense-categories'],
    queryFn:  () => depensesApi.listCategories(),
  })

  const toastOk  = (title: string, message: string) => addNotification({ title, message, type: 'success' })
  const toastErr = (e: any) => addNotification({ title: 'Erreur', message: e?.response?.data?.message ?? 'Une erreur est survenue', type: 'error' })
  const refresh  = () => { qc.invalidateQueries({ queryKey: ['depense', id] }); qc.invalidateQueries({ queryKey: ['depenses'] }) }

  const submitM = useMutation({
    mutationFn: () => depensesApi.submit(id!),
    onSuccess: () => { toastOk('Fiche soumise', 'La dépense est soumise pour validation.'); refresh() },
    onError: toastErr,
  })
  const validateM = useMutation({
    mutationFn: () => depensesApi.validate(id!),
    onSuccess: () => { toastOk('Fiche validée', 'La dépense a été approuvée.'); refresh() },
    onError: toastErr,
  })
  const rejectM = useMutation({
    mutationFn: () => depensesApi.reject(id!, rejectReason),
    onSuccess: () => { toastOk('Fiche rejetée', 'La dépense a été rejetée.'); setRejectOpen(false); setRejectReason(''); refresh() },
    onError: toastErr,
  })
  const payM = useMutation({
    mutationFn: () => depensesApi.pay(id!, chargeAccountId || undefined),
    onSuccess: () => { toastOk('Dépense payée', 'Le paiement est enregistré et comptabilisé (écriture DRAFT).'); setPayOpen(false); refresh() },
    onError: toastErr,
  })
  const justifyM = useMutation({
    mutationFn: () => depensesApi.justify(id!),
    onSuccess: () => { toastOk('Avance régularisée', 'La fiche passe au statut justifiée.'); refresh() },
    onError: toastErr,
  })

  // [B32-b] Justificatifs — upload / suppression
  const fileInputRef = useRef<HTMLInputElement>(null)
  const attachM = useMutation({
    mutationFn: (file: File) => depensesApi.attachJustificatif(id!, file),
    onSuccess: (res) => {
      // Après paiement, le message dit si la pièce rejoint l'archive légale (via l'écriture).
      if (exp && isPaidPhase(exp.status) && res?.attachedToEntry === true) {
        toastOk('Justificatif joint', "Justificatif ajouté et rattaché à l'écriture")
      } else if (exp && isPaidPhase(exp.status) && res?.attachedToEntry === false) {
        toastOk('Justificatif joint', 'Justificatif ajouté (écriture comptable non modifiable ou absente : pièce conservée, non archivée)')
      } else {
        toastOk('Justificatif joint', 'Le fichier a été ajouté à la fiche.')
      }
      refresh()
    },
    onError: toastErr,
  })
  const detachM = useMutation({
    mutationFn: (attId: string) => depensesApi.deleteJustificatif(id!, attId),
    onSuccess: () => { toastOk('Justificatif retiré', 'Le fichier a été supprimé.'); refresh() },
    onError: toastErr,
  })

  // Justificatifs : aperçu dans la visionneuse (URL « inline ») ou téléchargement
  // réel (URL « attachment », sans ouverture d'onglet donc sans blocage pop-up).
  const [preview, setPreview] = useState<ExpenseAttachment | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const downloadJustificatif = async (attId: string) => {
    if (downloadingId) return // évite le double clic
    setDownloadingId(attId)
    try {
      const { url } = await depensesApi.getJustificatifUrl(id!, attId, 'attachment')
      downloadFromUrl(url)
    } catch (e) {
      toastErr(e)
    } finally {
      setDownloadingId(null)
    }
  }

  const handleAttachFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // permet de re-sélectionner le même fichier
    if (!file) return
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    if (!ATTACH_ALLOWED_EXT.includes(ext)) {
      return toastErr({ response: { data: { message: `Format non supporté. Acceptés : ${ATTACH_ALLOWED_EXT.join(', ').toUpperCase()}.` } } })
    }
    if (file.size > ATTACH_MAX_MB * 1024 * 1024) {
      return toastErr({ response: { data: { message: `Fichier trop volumineux (max ${ATTACH_MAX_MB} Mo).` } } })
    }
    attachM.mutate(file)
  }

  if (isLoading) return <div className="flex items-center justify-center p-12"><Spinner /></div>
  if (isError || !exp) {
    return (
      <div className="p-12 text-center">
        <p className="text-[var(--tx3)]">Fiche de dépense introuvable.</p>
        <button onClick={() => navigate('/depenses')} className="mt-3 text-sm text-[var(--blue)] hover:underline">← Retour à la liste</button>
      </div>
    )
  }

  // Le libellé de catégorie vient de findOne (exp.category) — disponible même si
  // la catégorie est désactivée. La liste des actives sert uniquement à retrouver
  // le compte de charge pour le pré-remplissage au paiement.
  const categoryFromList = exp.categoryId ? categories?.find(c => c.id === exp.categoryId) : undefined

  // Acteurs : nom résolu par l'API, identifiant abrégé en repli (compte introuvable).
  const timeline: Array<{ label: string; at: string | null; who?: string | null; whoIsId?: boolean }> = [
    { label: 'Créée',    at: exp.createdAt,   who: personName(exp.createdBy, exp.createdById), whoIsId: !exp.createdBy },
    { label: 'Soumise',  at: exp.submittedAt },
    { label: 'Validée',  at: exp.validatedAt, who: exp.validatedById ? personName(exp.validatedBy, exp.validatedById) : null, whoIsId: !exp.validatedBy },
    { label: 'Payée',    at: exp.paidAt,      who: exp.paidById      ? personName(exp.paidBy,      exp.paidById)      : null, whoIsId: !exp.paidBy },
  ]

  // « Pas juge et partie » : le créateur ne peut ni valider ni rejeter sa fiche.
  const isCreator = !!currentUser && currentUser.id === exp.createdById
  const busy = submitM.isPending || validateM.isPending || rejectM.isPending || payM.isPending || justifyM.isPending

  // Actions disponibles = permission ET état ET (règle métier). Le backend reste
  // la vraie barrière : ces conditions ne font que masquer/afficher (confort UX).
  const canSubmit   = exp.status === 'DRAFT'     && isCreator && can('depenses:create')
  const canValidate = exp.status === 'SUBMITTED' && can('depenses:validate') && !isCreator
  const canPay      = exp.status === 'APPROVED'  && can('depenses:pay')
  const canJustify  = exp.status === 'PAID'      && exp.type === 'AVANCE' && isCreator && can('depenses:create')

  // [B32-b] Justificatifs : joindre dans tout statut sauf REJECTED. Avant paiement :
  // depenses:create ; après paiement : créateur de la fiche ou détenteur de depenses:pay.
  // Retirer seulement une pièce NON scellée, avant paiement (miroir du backend —
  // vraie barrière côté serveur).
  const paidPhase    = isPaidPhase(exp.status)
  const canAttachNow = canAttachExpense(exp) && (
    paidPhase
      ? (isCreator && can('depenses:create')) || can('depenses:pay')
      : can('depenses:create')
  )
  const canDetachNow = can('depenses:create') && ['DRAFT', 'SUBMITTED', 'APPROVED'].includes(exp.status)

  const btnBase = 'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50'

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/depenses')} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--brand-dark)] font-mono">{exp.reference}</h1>
            <p className="text-sm text-[var(--tx3)]">Demandeur : {requester(exp)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={exp.status} />
          {canSubmit && (
            <button disabled={busy} onClick={() => submitM.mutate()} className={`${btnBase} bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white`}>
              <Send className="w-4 h-4" /> Soumettre
            </button>
          )}
          {canValidate && (
            <>
              <button disabled={busy} onClick={() => validateM.mutate()} className={`${btnBase} bg-[#10B981] hover:bg-emerald-600 text-white`}>
                <Check className="w-4 h-4" /> Valider
              </button>
              <button disabled={busy} onClick={() => setRejectOpen(true)} className={`${btnBase} bg-white border border-[rgba(239,68,68,0.35)] text-[#DC2626] hover:bg-red-50`}>
                <X className="w-4 h-4" /> Rejeter
              </button>
            </>
          )}
          {canPay && (
            <button disabled={busy} onClick={() => { setChargeAccountId(exp.chargeAccountId ?? categoryFromList?.chargeAccountId ?? ''); setPayOpen(true) }} className={`${btnBase} bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white`}>
              <Banknote className="w-4 h-4" /> Payer
            </button>
          )}
          {canJustify && (
            <button disabled={busy} onClick={() => justifyM.mutate()} className={`${btnBase} bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white`}>
              <ShieldCheck className="w-4 h-4" /> Régulariser
            </button>
          )}
        </div>
      </div>

      {/* Infos principales */}
      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-5 grid grid-cols-2 md:grid-cols-3 gap-5">
        <InfoRow label="Montant" value={<span className="font-bold">{fmtMoney(exp.amount)}</span>} />
        <InfoRow label="Mode de paiement" value={PAYMENT_LABELS[exp.paymentMode] ?? exp.paymentMode} />
        <InfoRow label="Compte de charge" value={exp.chargeAccountId ? shortId(exp.chargeAccountId) : 'Non classé'} mono={!!exp.chargeAccountId} />
        <InfoRow label="Catégorie" value={
          exp.category
            ? `${exp.category.label}${exp.category.isActive ? '' : ' (inactive)'}`
            : exp.categoryId ? shortId(exp.categoryId) : '—'
        } />
        <InfoRow label="Demandeur" value={
          exp.employee
            ? <>{requester(exp)} <span className="font-mono text-xs text-[var(--tx3)]">· {exp.employee.matricule}</span></>
            : requester(exp)
        } mono={requesterIsId(exp)} />
        <InfoRow label="Créée par" value={personName(exp.createdBy, exp.createdById)} mono={!exp.createdBy} />
        <InfoRow label="Écriture comptable" value={exp.journalEntryId ? shortId(exp.journalEntryId) : '—'} mono={!!exp.journalEntryId} />
        <div className="col-span-2 md:col-span-3">
          <InfoRow label="Motif" value={exp.description} />
        </div>
      </div>

      {/* Historique de statut */}
      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-5">
        <p className="text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-3">Historique</p>
        <div className="space-y-2">
          {timeline.map(step => (
            <div key={step.label} className="flex items-center gap-3 text-sm">
              <span className={`w-2 h-2 rounded-full shrink-0 ${step.at ? 'bg-[var(--blue)]' : 'bg-[var(--tx3)]/30'}`} />
              <span className="w-20 text-[var(--tx3)] text-xs">{step.label}</span>
              <span className="text-[var(--brand-dark)]">{fmtDateTime(step.at)}</span>
              {step.who && <span className={`text-xs text-[var(--tx3)] ${step.whoIsId ? 'font-mono' : ''}`}>· {step.who}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Justificatifs */}
      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-[var(--tx3)]" />
            <p className="text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)]">
              Justificatifs {exp.justificatifs?.length ? `(${exp.justificatifs.length})` : ''}
            </p>
          </div>
          {canAttachNow && (
            <>
              <input ref={fileInputRef} type="file" accept={ATTACH_ACCEPT} className="hidden" onChange={handleAttachFile} />
              <button
                disabled={attachM.isPending}
                onClick={() => fileInputRef.current?.click()}
                aria-describedby={paidPhase ? 'attach-after-payment-hint' : undefined}
                className={`${btnBase} bg-white border border-[var(--bd)] text-[var(--tx2)] hover:bg-[var(--surface-2)] py-1.5`}
              >
                <Upload className="w-4 h-4" aria-hidden="true" /> {attachM.isPending ? 'Envoi…' : 'Joindre'}
              </button>
            </>
          )}
        </div>
        {canAttachNow && paidPhase && (
          <p id="attach-after-payment-hint" className="text-xs text-[var(--tx3)] -mt-1 mb-3">
            Les justificatifs reçus après paiement sont acceptés ; ils sont archivés avec l'écriture comptable si elle n'est pas encore validée.
          </p>
        )}
        {!exp.justificatifs?.length ? (
          <p className="text-sm text-[var(--tx3)]">Aucun justificatif attaché.</p>
        ) : (
          <ul className="space-y-2">
            {exp.justificatifs.map(j => {
              const archive = archiveState(j, exp.status)
              return (
                <li key={j.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm bg-white rounded-lg px-3 py-2 border border-[var(--bd)]">
                  <FileText className="w-4 h-4 text-[var(--tx3)] shrink-0" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => setPreview(j)}
                      title="Aperçu de ce justificatif"
                      className="block max-w-full text-left text-[var(--brand-dark)] hover:text-[var(--blue)] hover:underline truncate"
                    >
                      {j.fileName}
                    </button>
                    <p className="text-[11px] text-[var(--tx3)]">
                      Ajouté le <time dateTime={j.createdAt}>{fmtDateTime(j.createdAt)}</time> · {fmtSize(j.fileSize)}
                    </p>
                  </div>
                  {archive && <ArchiveBadge state={archive} />}
                  <button
                    onClick={() => setPreview(j)}
                    title="Aperçu"
                    aria-label={`Aperçu de ${j.fileName}`}
                    className="text-[var(--tx3)] hover:text-[var(--blue)]"
                  >
                    <Eye className="w-4 h-4" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => downloadJustificatif(j.id)}
                    disabled={downloadingId === j.id}
                    title="Télécharger"
                    aria-label={`Télécharger ${j.fileName}`}
                    className="text-[var(--tx3)] hover:text-[var(--blue)] disabled:opacity-40 disabled:cursor-wait"
                  >
                    <Download className="w-4 h-4" aria-hidden="true" />
                  </button>
                  {!archive && canDetachNow && (
                    <button
                      disabled={detachM.isPending}
                      onClick={() => detachM.mutate(j.id)}
                      title="Retirer ce justificatif"
                      aria-label={`Retirer ${j.fileName}`}
                      className="text-[var(--tx3)] hover:text-[#DC2626] disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* Visionneuse des justificatifs (lecture seule) */}
      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        fileName={preview?.fileName ?? ''}
        mimeType={preview?.mimeType}
        fileSize={preview?.fileSize}
        getInlineUrl={() => depensesApi.getJustificatifUrl(id!, preview!.id, 'inline')}
        getDownloadUrl={() => depensesApi.getJustificatifUrl(id!, preview!.id, 'attachment').then(r => r.url)}
      />

      {/* Modale — Rejet (motif obligatoire) */}
      {rejectOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setRejectOpen(false)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-[var(--brand-dark)]">Rejeter la fiche {exp.reference}</h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Motif du rejet (obligatoire)…"
              rows={3}
              className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRejectOpen(false)} className="px-3 py-2 rounded-lg text-sm text-[var(--tx2)] hover:bg-[var(--surface-2)]">Annuler</button>
              <button
                disabled={busy || rejectReason.trim().length < 3}
                onClick={() => rejectM.mutate()}
                className={`${btnBase} bg-[#DC2626] hover:bg-red-700 text-white`}
              >
                <X className="w-4 h-4" /> Confirmer le rejet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale — Paiement (sélecteur du compte de charge classe 6) */}
      {payOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPayOpen(false)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-[var(--brand-dark)]">Payer {exp.reference} — {fmtMoney(exp.amount)}</h3>
            <div>
              <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Compte de charge (classe 6)</label>
              <select
                value={chargeAccountId}
                onChange={e => setChargeAccountId(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] bg-white focus:outline-none focus:border-blue-500"
              >
                <option value="">— Sélectionner un compte —</option>
                {(chargeAccounts ?? []).map(a => (
                  <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                ))}
              </select>
              <p className="text-xs text-[var(--tx3)] mt-1">Le compte de charge est obligatoire pour comptabiliser le décaissement.</p>
              {!exp.chargeAccountId && categoryFromList?.chargeAccountId && (
                <p className="text-xs text-[var(--blue)] mt-1">Pré-rempli depuis la catégorie « {exp.category?.label ?? categoryFromList.label} ». Vous pouvez le modifier.</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPayOpen(false)} className="px-3 py-2 rounded-lg text-sm text-[var(--tx2)] hover:bg-[var(--surface-2)]">Annuler</button>
              <button
                disabled={busy || !chargeAccountId}
                onClick={() => payM.mutate()}
                className={`${btnBase} bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white`}
              >
                <Banknote className="w-4 h-4" /> Confirmer le paiement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  CRÉATION
// ============================================================================

function DepensesCreatePage() {
  const navigate = useNavigate()
  const { addNotification } = useNotificationStore()
  const qc = useQueryClient()

  // Le type de dépense (AVANCE / REMBOURSEMENT / FACTURE_FOURNISSEUR) n'est plus
  // saisi : le backend applique REMBOURSEMENT (porte employé standard).
  const [form, setForm] = useState<{ description: string; amount: string; paymentMode: ExpensePaymentMode; categoryId: string }>({
    description: '', amount: '', paymentMode: 'CASH', categoryId: '',
  })

  // Catégories actives (libellé seul pour l'employé — jamais le numéro de compte).
  const { data: categories } = useQuery({
    queryKey: ['depense-categories'],
    queryFn:  () => depensesApi.listCategories(),
  })

  const createM = useMutation({
    mutationFn: () => {
      const payload: CreateExpenseInput = {
        description: form.description.trim(),
        amount: Number(form.amount),
        paymentMode: form.paymentMode,
        categoryId: form.categoryId,
      }
      return depensesApi.create(payload)
    },
    onSuccess: (exp) => {
      addNotification({ title: 'Dépense créée', message: `Fiche ${exp.reference} enregistrée.`, type: 'success' })
      qc.invalidateQueries({ queryKey: ['depenses'] })
      navigate(`/depenses/${exp.id}`)
    },
    onError: (e: any) => addNotification({ title: 'Erreur', message: e?.response?.data?.message ?? 'Création impossible', type: 'error' }),
  })

  const amountNum = Number(form.amount)
  // Catégorie obligatoire : elle détermine le compte de charge de l'écriture comptable
  const valid = form.description.trim().length >= 3 && amountNum >= 1 && !!form.categoryId
  const noCategory = Array.isArray(categories) && categories.length === 0

  return (
    <div className="p-6 space-y-4 max-w-2xl">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/depenses')} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-xl font-bold text-[var(--brand-dark)]">Nouvelle dépense</h1>
      </div>

      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-5 space-y-4">
        <div>
          <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">
            Catégorie <span className="text-[#DC2626]">*</span>
          </label>
          <select
            required
            value={form.categoryId}
            onChange={e => setForm(f => ({ ...f, categoryId: e.target.value }))}
            className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] bg-white focus:outline-none focus:border-blue-500"
          >
            <option value="" disabled>— Sélectionner une catégorie —</option>
            {(categories ?? []).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          {noCategory ? (
            <p className="text-xs text-[#DC2626] mt-1">
              Aucune catégorie active. Demandez à la comptabilité d'en créer une (Dépenses → Catégories) avant de saisir une dépense.
            </p>
          ) : (
            <p className="text-xs text-[var(--tx3)] mt-1">Obligatoire. La catégorie détermine le compte de charge de l'écriture comptable.</p>
          )}
        </div>

        <div>
          <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Motif</label>
          <textarea
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Objet de la dépense (min. 3 caractères)…"
            rows={2}
            className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Montant (XOF)</label>
            <input
              type="number" min={1}
              value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              placeholder="0"
              className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Mode de paiement</label>
            <select
              value={form.paymentMode}
              onChange={e => setForm(f => ({ ...f, paymentMode: e.target.value as ExpensePaymentMode }))}
              className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] bg-white focus:outline-none focus:border-blue-500"
            >
              <option value="CASH">Espèces (caisse)</option>
              <option value="BANK">Virement (banque)</option>
            </select>
          </div>
        </div>

        <p className="text-xs text-[var(--tx3)]">
          Le compte de charge est déduit de la catégorie et confirmé par le comptable au paiement. Les justificatifs se joignent depuis la fiche, une fois la dépense créée.
        </p>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={() => navigate('/depenses')} className="px-3 py-2 rounded-lg text-sm text-[var(--tx2)] hover:bg-white">Annuler</button>
          <button
            disabled={!valid || createM.isPending}
            onClick={() => createM.mutate()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white transition-colors disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> Créer la dépense
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
//  GESTION DES CATÉGORIES (admin / comptable — depenses:manage_categories)
// ============================================================================

function DepensesCategoriesPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { can } = usePermission()
  const { addNotification } = useNotificationStore()

  const canManage = can('depenses:manage_categories')

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<ExpenseCategory | null>(null)
  const [form, setForm] = useState<{ label: string; chargeAccountId: string }>({ label: '', chargeAccountId: '' })

  // Écran de gestion : on inclut les inactives (clé de cache distincte de la liste
  // des actives utilisée par la création/le détail, pour ne pas les polluer).
  const { data: categories, isLoading, isError } = useQuery({
    queryKey: ['depense-categories', 'all'],
    queryFn:  () => depensesApi.listCategories(true),
    enabled:  canManage,
  })

  // Comptes de charge CLASSE 6 existants (jamais de champ libre : on ne crée aucun compte).
  const { data: chargeAccounts } = useQuery({
    queryKey: ['charge-accounts'],
    queryFn:  () => accountingApi.getAccounts({ class: 6, isActive: true }),
    enabled:  canManage,
  })

  const toastOk  = (title: string, message: string) => addNotification({ title, message, type: 'success' })
  const toastErr = (e: any) => addNotification({ title: 'Erreur', message: e?.response?.data?.message ?? 'Une erreur est survenue', type: 'error' })
  const refresh  = () => qc.invalidateQueries({ queryKey: ['depense-categories'] })

  const saveM = useMutation({
    mutationFn: () => {
      const data = { label: form.label.trim(), chargeAccountId: form.chargeAccountId }
      return editing ? depensesApi.updateCategory(editing.id, data) : depensesApi.createCategory(data)
    },
    onSuccess: () => {
      toastOk(editing ? 'Catégorie modifiée' : 'Catégorie créée', 'Le catalogue des catégories est à jour.')
      setModalOpen(false); refresh()
    },
    onError: toastErr,
  })

  const deactivateM = useMutation({
    mutationFn: (id: string) => depensesApi.deleteCategory(id),
    onSuccess: () => { toastOk('Catégorie désactivée', 'Elle n\'est plus proposée à la création d\'une dépense.'); refresh() },
    onError: toastErr,
  })

  const reactivateM = useMutation({
    mutationFn: (id: string) => depensesApi.updateCategory(id, { isActive: true }),
    onSuccess: () => { toastOk('Catégorie réactivée', 'Elle est de nouveau proposée à la création d\'une dépense.'); refresh() },
    onError: toastErr,
  })

  const openCreate = () => { setEditing(null); setForm({ label: '', chargeAccountId: '' }); setModalOpen(true) }
  const openEdit = (c: ExpenseCategory) => { setEditing(c); setForm({ label: c.label, chargeAccountId: c.chargeAccountId }); setModalOpen(true) }

  const accountLabel = (id: string) => {
    const a = chargeAccounts?.find(x => x.id === id)
    return a ? `${a.code} — ${a.name}` : shortId(id)
  }

  const valid = form.label.trim().length >= 2 && !!form.chargeAccountId

  // Écran réservé : masqué si l'utilisateur n'a pas la permission de gestion.
  if (!canManage) {
    return (
      <div className="p-12 text-center">
        <p className="text-[var(--tx3)]">Vous n'avez pas accès à la gestion des catégories de dépense.</p>
        <button onClick={() => navigate('/depenses')} className="mt-3 text-sm text-[var(--blue)] hover:underline">← Retour aux dépenses</button>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/depenses')} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <Tags className="w-5 h-5 text-[var(--brand-dark)]" />
            <h1 className="text-xl font-bold text-[var(--brand-dark)]">Catégories de dépense</h1>
            {categories && <span className="text-xs text-[var(--tx3)] bg-white px-2 py-0.5 rounded-full">{categories.length}</span>}
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" /> Nouvelle catégorie
        </button>
      </div>

      <p className="text-xs text-[var(--tx3)]">
        Chaque catégorie pointe vers un compte de charge SYSCOHADA classe 6 existant. La désactivation est un soft delete : la catégorie n'est plus proposée à la création, mais les dépenses déjà rattachées sont conservées et elle reste réactivable.
      </p>

      {/* Table */}
      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center p-12"><Spinner /></div>
        ) : isError ? (
          <div className="p-12 text-center"><p className="text-[var(--tx3)]">Impossible de charger les catégories.</p></div>
        ) : !categories?.length ? (
          <div className="p-12 text-center">
            <Tags className="w-8 h-8 text-[var(--tx3)] mx-auto mb-3" />
            <p className="text-[var(--tx3)]">Aucune catégorie. Créez-en une pour aider les employés à classer leurs dépenses.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Libellé', 'Compte de charge', 'Statut', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs text-[var(--tx3)] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categories.map(c => (
                <tr key={c.id} className={`border-b border-[var(--bd)] ${c.isActive ? '' : 'opacity-60'}`}>
                  <td className="px-4 py-3 font-medium text-[var(--brand-dark)]">{c.label}</td>
                  <td className="px-4 py-3 text-[var(--tx3)]">{accountLabel(c.chargeAccountId)}</td>
                  <td className="px-4 py-3">
                    {c.isActive ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-[#10B981]/20 text-emerald-300 border-emerald-500/30">Active</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-[#D6D2EC]/20 text-[var(--tx2)] border-[var(--tx3)]/30">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(c)}
                        title="Modifier"
                        className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-[var(--brand-dark)] hover:bg-white transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {c.isActive ? (
                        <button
                          disabled={deactivateM.isPending}
                          onClick={() => { if (window.confirm(`Désactiver la catégorie « ${c.label} » ? Elle ne sera plus proposée à la création d'une dépense.`)) deactivateM.mutate(c.id) }}
                          title="Désactiver"
                          className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-[#DC2626] hover:bg-white transition-colors disabled:opacity-50"
                        >
                          <Ban className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          disabled={reactivateM.isPending}
                          onClick={() => reactivateM.mutate(c.id)}
                          title="Réactiver"
                          className="p-1.5 rounded-lg text-[var(--tx3)] hover:text-emerald-600 hover:bg-white transition-colors disabled:opacity-50"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modale — Création / modification */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setModalOpen(false)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-[var(--brand-dark)]">{editing ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</h3>
            <div>
              <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Libellé</label>
              <input
                value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                placeholder="Ex. Transport, Restauration…"
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold tracking-[0.08em] uppercase text-[var(--tx3)] mb-1">Compte de charge (classe 6)</label>
              <select
                value={form.chargeAccountId}
                onChange={e => setForm(f => ({ ...f, chargeAccountId: e.target.value }))}
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] bg-white focus:outline-none focus:border-blue-500"
              >
                <option value="">— Sélectionner un compte —</option>
                {(chargeAccounts ?? []).map(a => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
              </select>
              <p className="text-xs text-[var(--tx3)] mt-1">Compte existant du plan SYSCOHADA — aucun compte n'est créé ici.</p>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setModalOpen(false)} className="px-3 py-2 rounded-lg text-sm text-[var(--tx2)] hover:bg-[var(--surface-2)]">Annuler</button>
              <button
                disabled={!valid || saveM.isPending}
                onClick={() => saveM.mutate()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white transition-colors disabled:opacity-50"
              >
                <Check className="w-4 h-4" /> {editing ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  ROUTES DU MODULE
// ============================================================================

export default function DepensesPages() {
  return (
    <Routes>
      <Route path="" element={<DepensesListPage />} />
      <Route path="nouvelle" element={<DepensesCreatePage />} />
      <Route path="categories" element={<DepensesCategoriesPage />} />
      <Route path=":id" element={<DepensesDetailPage />} />
    </Routes>
  )
}
